import React, { useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Stack,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
} from "@mui/material";

// Icons
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import BadgeIcon from "@mui/icons-material/Badge";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import SecurityIcon from "@mui/icons-material/Security";
import SettingsIcon from "@mui/icons-material/Settings";

import MuiPageHeader from "../../components/mui/MuiPageHeader";
import { editProfile, resetProfileFlag } from "../../store/actions";

const MuiUserProfile = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  document.title = t("Profile") + " | FlexFlow ERP";

  // Memoized selectors to avoid unnecessary rerenders
  const error = useSelector((state) => state.Profile?.error);
  const success = useSelector((state) => state.Profile?.success);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [idx, setIdx] = useState("");
  const [newUsername, setNewUsername] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const authUser = localStorage.getItem("authUser");
    if (authUser) {
      const obj = JSON.parse(authUser);
      const authType = import.meta.env.VITE_DEFAULTAUTH;

      if (authType === "firebase") {
        setName(obj.displayName || "");
        setEmail(obj.email || "");
        setIdx(obj.uid || "");
      } else if (authType === "fake" || authType === "jwt") {
        setName(obj.username || obj.first_name || "");
        setEmail(obj.email || "");
        setIdx(obj.uid || obj.id || "");
      }
    }
  }, []);

  useEffect(() => {
    if (success) {
      setSnackbar({ open: true, message: t("Profile updated successfully"), severity: "success" });
      setIsEditing(false);
      setTimeout(() => {
        dispatch(resetProfileFlag());
      }, 3000);
    }
    if (error) {
      setSnackbar({ open: true, message: error, severity: "error" });
    }
  }, [success, error, dispatch, t]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (newUsername.trim().length >= 3) {
        dispatch(editProfile({ username: newUsername, idx }));
      }
    },
    [dispatch, newUsername, idx]
  );

  const handleStartEdit = () => {
    setNewUsername(name);
    setIsEditing(true);
  };

  const breadcrumbs = [{ label: t("Profile") }];

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box sx={{ py: 3, bgcolor: "#f9fafb", minHeight: "100vh" }}>
      <Container maxWidth="lg">
        <MuiPageHeader
          title={t("Profile")}
          subtitle={t("Manage your account settings")}
          breadcrumbs={breadcrumbs}
        />

        <Grid container spacing={3}>
          {/* Left Column - Profile Card */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                overflow: "hidden",
              }}
            >
              {/* Profile Header with Gradient */}
              <Box
                sx={{
                  height: 100,
                  background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                  position: "relative",
                }}
              />

              <CardContent sx={{ pt: 0, pb: 3, px: 3, textAlign: "center" }}>
                {/* Avatar */}
                <Box sx={{ mt: -6, mb: 2, position: "relative", display: "inline-block" }}>
                  <Avatar
                    sx={{
                      width: 96,
                      height: 96,
                      fontSize: "2rem",
                      fontWeight: 600,
                      bgcolor: "#3b82f6",
                      border: "4px solid #fff",
                      boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
                    }}
                  >
                    {getInitials(name)}
                  </Avatar>
                  <IconButton
                    size="small"
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      bgcolor: "#fff",
                      border: "2px solid #e5e7eb",
                      "&:hover": { bgcolor: "#f3f4f6" },
                    }}
                  >
                    <CameraAltIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                {/* Name & Email */}
                <Typography variant="h6" fontWeight={600} color="#111827">
                  {name || t("User")}
                </Typography>
                <Typography variant="body2" color="#6b7280" sx={{ mt: 0.5 }}>
                  {email || t("No email")}
                </Typography>

                {/* ID Badge */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 2,
                    px: 2,
                    py: 0.5,
                    bgcolor: "#f3f4f6",
                    borderRadius: 2,
                  }}
                >
                  <BadgeIcon sx={{ fontSize: 14, color: "#6b7280" }} />
                  <Typography variant="caption" color="#6b7280" fontWeight={500}>
                    ID: #{idx || "N/A"}
                  </Typography>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Quick Stats */}
                <Stack spacing={2}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "#dbeafe", color: "#3b82f6" }}>
                      <PersonIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="caption" color="#6b7280">
                        {t("Account Type")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color="#111827">
                        {t("Administrator")}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "#d1fae5", color: "#10b981" }}>
                      <SecurityIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Box sx={{ textAlign: "left" }}>
                      <Typography variant="caption" color="#6b7280">
                        {t("Status")}
                      </Typography>
                      <Typography variant="body2" fontWeight={500} color="#10b981">
                        {t("Active")}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column - Edit Profile */}
          <Grid item xs={12} md={8}>
            {/* Alerts */}
            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            {/* Edit Profile Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb", mb: 3 }}>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    px: 3,
                    py: 2,
                    borderBottom: "1px solid #f3f4f6",
                    bgcolor: "#fafafa",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "#dbeafe", color: "#3b82f6" }}>
                      <EditIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">
                      {t("Edit Profile")}
                    </Typography>
                  </Stack>
                  {!isEditing && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={handleStartEdit}
                      sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                      {t("Edit")}
                    </Button>
                  )}
                </Box>

                <Box sx={{ p: 3 }}>
                  <form onSubmit={handleSubmit}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t("Username")}
                          value={isEditing ? newUsername : name}
                          onChange={(e) => setNewUsername(e.target.value)}
                          disabled={!isEditing}
                          error={isEditing && newUsername.length > 0 && newUsername.length < 3}
                          helperText={
                            isEditing && newUsername.length > 0 && newUsername.length < 3
                              ? t("Username must be at least 3 characters")
                              : ""
                          }
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PersonIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: isEditing ? "#fff" : "#f9fafb",
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t("Email")}
                          value={email}
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: "#f9fafb",
                            },
                          }}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={t("User ID")}
                          value={idx ? `#${idx}` : ""}
                          disabled
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <BadgeIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: 2,
                              bgcolor: "#f9fafb",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>

                    {isEditing && (
                      <Stack direction="row" spacing={2} sx={{ mt: 3, justifyContent: "flex-end" }}>
                        <Button
                          variant="outlined"
                          onClick={() => setIsEditing(false)}
                          sx={{ borderRadius: 2, textTransform: "none" }}
                        >
                          {t("Cancel")}
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={<SaveIcon />}
                          disabled={newUsername.length < 3}
                          sx={{
                            borderRadius: 2,
                            textTransform: "none",
                            bgcolor: "#3b82f6",
                            "&:hover": { bgcolor: "#2563eb" },
                          }}
                        >
                          {t("Save Changes")}
                        </Button>
                      </Stack>
                    )}
                  </form>
                </Box>
              </CardContent>
            </Card>

            {/* Account Settings Card */}
            <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e5e7eb" }}>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #f3f4f6", bgcolor: "#fafafa" }}>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar sx={{ width: 36, height: 36, bgcolor: "#f3e8ff", color: "#8b5cf6" }}>
                      <SettingsIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="subtitle1" fontWeight={600} color="#111827">
                      {t("Account Settings")}
                    </Typography>
                  </Stack>
                </Box>

                <Box sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    {[
                      { label: t("Change Password"), description: t("Update your password regularly for security") },
                      { label: t("Two-Factor Authentication"), description: t("Add an extra layer of security") },
                      { label: t("Notification Preferences"), description: t("Manage your notification settings") },
                    ].map((item, index) => (
                      <Grid item xs={12} key={index}>
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid #e5e7eb",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: "#f9fafb",
                              borderColor: "#d1d5db",
                            },
                          }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={500} color="#111827">
                              {item.label}
                            </Typography>
                            <Typography variant="caption" color="#6b7280">
                              {item.description}
                            </Typography>
                          </Box>
                          <Button size="small" sx={{ textTransform: "none", color: "#3b82f6" }}>
                            {t("Configure")}
                          </Button>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MuiUserProfile;
